import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getEnv } from '../../lib/env';

type Status = 'pending' | 'processing' | 'completed' | 'error';

interface Company {
  id: string;
  name: string;
  tenant_id?: string;
  slug?: string | null;
}

interface EntitySelection {
  customers: boolean;
  jobs: boolean;
  agreements: boolean;
  assignments: boolean;
}

interface EntityProgress {
  entity: keyof EntitySelection;
  status: Status;
  processed: number;
  imported: number;
  skipped: number;
  failed: number;
  pages: number;
  error_message?: string;
}

interface CompanyProgress {
  company_id: string;
  company_name: string;
  status: Status;
  entities: Record<keyof EntitySelection, EntityProgress>;
}

export default function IncrementalTrueUpPage() {
  const env = getEnv();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [entitySelection, setEntitySelection] = useState<EntitySelection>({
    customers: true,
    jobs: true,
    agreements: true,
    assignments: true,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [running, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<CompanyProgress[]>([]);
  const anySelected = useMemo(
    () => selectedCompanies.length > 0 && Object.values(entitySelection).some(Boolean),
    [selectedCompanies, entitySelection]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('sw_companies')
        .select('id, name, slug, tenant_id')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Failed to load companies', error);
        alert('Failed to load companies');
      } else {
        setCompanies(data || []);
      }
      setLoading(false);
    })();
  }, []);

  const toggleCompany = (companyId: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  const toggleAllCompanies = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map((c) => c.id));
    }
  };

  const toggleEntity = (entity: keyof EntitySelection) => {
    setEntitySelection((prev) => ({ ...prev, [entity]: !prev[entity] }));
  };

  const setEntityStatus = (
    companyId: string,
    entity: keyof EntitySelection,
    updater: (prev: EntityProgress) => EntityProgress
  ) => {
    setProgress((prev) =>
      prev.map((cp) => {
        if (cp.company_id !== companyId) return cp;
        return {
          ...cp,
          entities: {
            ...cp.entities,
            [entity]: updater(cp.entities[entity]),
          },
        };
      })
    );
  };

  const initializeProgress = () => {
    const baseEntity = (entity: keyof EntitySelection): EntityProgress => ({
      entity,
      status: 'pending',
      processed: 0,
      imported: 0,
      skipped: 0,
      failed: 0,
      pages: 0,
    });

    const initial: CompanyProgress[] = selectedCompanies.map((companyId) => {
      const company = companies.find((c) => c.id === companyId);
      return {
        company_id: companyId,
        company_name: company?.name || 'Unknown',
        status: 'pending',
        entities: {
          customers: baseEntity('customers'),
          jobs: baseEntity('jobs'),
          agreements: baseEntity('agreements'),
          assignments: baseEntity('assignments'),
        },
      };
    });

    setProgress(initial);
  };

  type InvokeResult = {
    success?: boolean;
    total_processed?: number;
    imported_count?: number;
    skipped_count?: number;
    failed_count?: number;
    has_more_pages?: boolean;
    imported?: number;
    total_count?: number;
    error?: string;
  };

  const invokeFunction = async (
    path: string,
    body: Record<string, any>
  ): Promise<InvokeResult> => {
    const res = await fetch(`${env.supabaseUrl}/functions/v1/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${env.supabaseAnonKey}`,
      },
      body: JSON.stringify(body),
    });
    let json: any = {};
    try {
      json = await res.json();
    } catch {
      // ignore
    }
    if (!res.ok) {
      return { success: false, error: json?.error || `HTTP ${res.status}` };
    }
    return json;
  };

  const runEntityForCompany = async (
    companyId: string,
    entity: keyof EntitySelection,
    batchSize: number
  ) => {
    // Update status to processing
    setEntityStatus(companyId, entity, (prev) => ({ ...prev, status: 'processing' }));

    const mapping: Record<
      keyof EntitySelection,
      { fn: string; pageParam: string; sizeParam: string }
    > = {
      customers: { fn: 'successware-incremental-import', pageParam: 'page_number', sizeParam: 'batch_size' },
      jobs: { fn: 'successware-import-jobs', pageParam: 'page_number', sizeParam: 'batch_size' },
      agreements: { fn: 'successware-import-agreements', pageParam: 'page_number', sizeParam: 'batch_size' },
      assignments: { fn: 'successware-import-assignments', pageParam: 'page_number', sizeParam: 'batch_size' },
    };

    const meta = mapping[entity];
    let page = 0;
    let keepGoing = true;

    while (keepGoing) {
      const result = await invokeFunction(meta.fn, {
        company_id: companyId,
        [meta.sizeParam]: batchSize,
        [meta.pageParam]: page,
      });

      if (result.error) {
        setEntityStatus(companyId, entity, (prev) => ({
          ...prev,
          status: 'error',
          error_message: result.error,
        }));
        return;
      }

      // Normalize counters across functions
      const processed =
        (result.total_processed ?? 0) ||
        (result.total_count ?? 0);
      const imported =
        (result.imported_count ?? 0) ||
        (result.imported ?? 0);
      const skipped = result.skipped_count ?? 0;
      const failed = result.failed_count ?? 0;

      setEntityStatus(companyId, entity, (prev) => ({
        ...prev,
        processed: prev.processed + processed,
        imported: prev.imported + imported,
        skipped: prev.skipped + skipped,
        failed: prev.failed + failed,
        pages: prev.pages + 1,
      }));

      keepGoing = !!result.has_more_pages;
      page += 1;

      // Throttle slightly between pages
      if (keepGoing) {
        await new Promise((r) => setTimeout(r, 800));
      }
    }

    // Mark entity completed
    setEntityStatus(companyId, entity, (prev) => ({ ...prev, status: 'completed' }));
  };

  const startTrueUp = async () => {
    if (!anySelected) {
      alert('Select at least one company and one entity.');
      return;
    }

    setRunning(true);
    initializeProgress();

    const selectedEntities = (Object.keys(entitySelection) as (keyof EntitySelection)[]).filter(
      (k) => entitySelection[k]
    );

    try {
      // Process companies sequentially to be gentle on rate limits
      for (const companyId of selectedCompanies) {
        // Mark company as processing
        setProgress((prev) =>
          prev.map((cp) => (cp.company_id === companyId ? { ...cp, status: 'processing' } : cp))
        );

        // Run entities sequentially per company
        for (const entity of selectedEntities) {
          await runEntityForCompany(companyId, entity, 100);
        }

        // Mark company completed
        setProgress((prev) =>
          prev.map((cp) => (cp.company_id === companyId ? { ...cp, status: 'completed' } : cp))
        );

        // Throttle between companies
        await new Promise((r) => setTimeout(r, 1500));
      }
    } catch (e: any) {
      console.error('True-up failed', e);
      alert(`True-up failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading companies...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">SuccessWare Incremental True-Up</h1>
        <p className="text-gray-600 mt-1">
          This runs per-company incremental imports for selected entities, only inserting records not already in your per-company tables.
        </p>
      </div>

      {/* Entity Selection */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Entities</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(Object.keys(entitySelection) as (keyof EntitySelection)[]).map((key) => (
            <label key={key} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={entitySelection[key]}
                onChange={() => toggleEntity(key)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-800 capitalize">{key}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Companies */}
      <div className="bg-white border rounded-lg">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Companies</h2>
          <button
            onClick={toggleAllCompanies}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {selectedCompanies.length === companies.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {companies.map((c) => (
            <label
              key={c.id}
              className={`flex items-center space-x-3 p-3 rounded border cursor-pointer ${
                selectedCompanies.includes(c.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => toggleCompany(c.id)}
            >
              <input
                type="checkbox"
                checked={selectedCompanies.includes(c.id)}
                onChange={() => toggleCompany(c.id)}
                className="h-4 w-4 text-blue-600"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">{c.name}</div>
                <div className="text-xs text-gray-500">ID: {c.id}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Start Button */}
      <div>
        <button
          onClick={startTrueUp}
          disabled={!anySelected || running}
          className="w-full md:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
        >
          {running ? 'Running...' : `Start True-Up (${selectedCompanies.length} compan${selectedCompanies.length === 1 ? 'y' : 'ies'})`}
        </button>
      </div>

      {/* Progress */}
      {progress.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-medium text-gray-900 mb-3">Progress</h2>
          <div className="space-y-4">
            {progress.map((cp) => (
              <div key={cp.company_id} className="border rounded p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-900">{cp.company_name}</div>
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      cp.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : cp.status === 'processing'
                        ? 'bg-blue-100 text-blue-800'
                        : cp.status === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {cp.status}
                  </span>
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  {(Object.keys(cp.entities) as (keyof EntitySelection)[]).map((entityKey) => {
                    const ep = cp.entities[entityKey];
                    return (
                      <div key={entityKey} className="border rounded p-2">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium capitalize">{entityKey}</div>
                          <span
                            className={`px-2 py-0.5 text-xs rounded ${
                              ep.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : ep.status === 'processing'
                                ? 'bg-blue-100 text-blue-800'
                                : ep.status === 'error'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {ep.status}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                          <div>Pages: {ep.pages}</div>
                          <div>Processed: {ep.processed}</div>
                          <div className="text-green-700">Imported: {ep.imported}</div>
                          <div className="text-yellow-700">Skipped: {ep.skipped}</div>
                          <div className="text-red-700 col-span-2">Failed: {ep.failed}</div>
                          {ep.error_message && (
                            <div className="col-span-2 text-red-600">Error: {ep.error_message}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
